"use client";
import { SENTRY_RELEASE_NAME } from "@/constants";
import { TokenUserInfo } from "@/entities/keycloak";
import { maskEmail } from "@/shared/lib/privacy";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

interface TelemetryConfig {
  sentry: {
    dsn: string | null;
    useSentrySaas: boolean;
    organization: string | null;
    projectId: number | null;
  };
  posthog: {
    key: string | null;
    apiHost: string | null;
  };
}

// Track initialization status
let isSentryInitialized = false;
let isPosthogInitialized = false;

// Set once the signed-in user is known. The PostHog `before_send` and Sentry
// `beforeSend` hooks run for events captured before `identify`/`setUser` have
// applied their properties, so they fall back to this.
let isTestUserSession = false;

// Check if an email belongs to a test user
function isTestUser(email: string): boolean {
  // Match pattern: test.*@fxcintel.com
  const testPattern = /^test.*@fxcintel\.com$/i;
  return testPattern.test(email);
}

export async function initTelemetry() {
  try {
    const resp = await fetch("/backend/telemetry");
    const config = (await resp.json()) as TelemetryConfig;

    // Initialize both services but track their status
    isSentryInitialized = initSentry(config.sentry);
    isPosthogInitialized = initPosthog(config.posthog);

    // If Sentry is initialized and PostHog is also initialized,
    // we can set up the integration
    if (isSentryInitialized && isPosthogInitialized) {
      setupPosthogSentryIntegration(config.sentry);
    }
  } catch (error) {
    console.error("Failed to initialize telemetry:", error);
  }
}

function initSentry({
  dsn,
  useSentrySaas,
}: TelemetryConfig["sentry"]): boolean {
  if (!dsn) {
    return false;
  }

  try {
    const baseConfig = {
      dsn,
      environment: "frontend",
      release: SENTRY_RELEASE_NAME,
      beforeSend(event: Sentry.ErrorEvent) {
        // Filter out test users
        const isTestUser = event.user?.isTestUser as boolean | undefined;
        if (isTestUser ?? isTestUserSession) {
          return null;
        }
        return event;
      },
    };

    if (!useSentrySaas) {
      Sentry.init({
        ...baseConfig,
        tunnel: "/backend/telemetry/sentry",
      });
    } else {
      Sentry.init({
        ...baseConfig,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.captureConsoleIntegration(),
          Sentry.contextLinesIntegration(),
          Sentry.extraErrorDataIntegration(),
          Sentry.httpClientIntegration(),
          /*Sentry.replayIntegration({
            maskAllText: false,
            mask: [".sensitive"],
          }),*/
          Sentry.reportingObserverIntegration(),
        ],
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        profilesSampleRate: 1.0,
      });
    }
    return true;
  } catch (error) {
    console.error("Failed to initialize Sentry:", error);
    return false;
  }
}

function initPosthog({ key, apiHost }: TelemetryConfig["posthog"]): boolean {
  if (!key || !apiHost) {
    return false;
  }

  try {
    posthog.init(key, {
      api_host: apiHost,
      person_profiles: "identified_only",
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: false,
        maskTextSelector: ".sensitive",
      },
      // Filter out test users
      before_send: (event) => {
        if (!event) {
          return null;
        }

        // Check if the event has email property and if it's a test user
        const properties = event.properties as
          | Record<string, unknown>
          | undefined;
        const setProps = properties?.$set as
          | Record<string, unknown>
          | undefined;
        const isTestUser =
          (setProps?.isTestUser as boolean) ||
          (properties?.isTestUser as boolean) ||
          isTestUserSession;

        if (isTestUser) {
          return null;
        }
        return event;
      },
    });
    return true;
  } catch (error) {
    console.error("Failed to initialize PostHog:", error);
    return false;
  }
}

function setupPosthogSentryIntegration({
  organization,
  projectId,
}: TelemetryConfig["sentry"]) {
  // Only add Sentry integration if we have both organization and projectId
  if (organization && projectId) {
    try {
      Sentry.addIntegration(
        posthog.sentryIntegration({
          organization,
          projectId,
          severityAllowList: ["error", "info"],
        }),
      );
    } catch (error) {
      console.error("Failed to setup PostHog-Sentry integration:", error);
    }
  }
}

export function attachUserInfoToTelemetry(
  userInfo: TokenUserInfo,
  clientInfo?: { uuid: string; name: string },
) {
  isTestUserSession = isTestUser(userInfo.email);

  // Set user info for Sentry only if it's initialized
  if (isSentryInitialized) {
    try {
      Sentry.setUser({
        id: userInfo.id,
        email: maskEmail(userInfo.email),
        role: userInfo.role,
        isTestUser: isTestUserSession,
      });
    } catch (error) {
      console.error("Failed to set user info in Sentry:", error);
    }
  }

  // Set user info for PostHog only if it's initialized
  if (isPosthogInitialized) {
    try {
      // `identify` sets person properties, which are not attached to
      // subsequent events. `register` makes it a super property so every
      // captured event carries it and `before_send` can filter on it.
      posthog.register({ isTestUser: isTestUserSession });

      posthog.identify(userInfo.id, {
        email: maskEmail(userInfo.email),
        role: userInfo.role,
        isTestUser: isTestUserSession,
      });

      if (clientInfo) {
        posthog.group("organization", clientInfo.uuid, {
          name: clientInfo.name,
        });
      }
    } catch (error) {
      console.error("Failed to set user info in PostHog:", error);
    }
  }
}
